"use client";

import { memo, useMemo } from "react";
import type { IFilters } from "../../../interfaces/StockList.interface";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";
import { useStockListingFilters } from "../context/StockListingFiltersContext";

type FilterKeyEntry = {
  filterKey: keyof IFilters;
  categoryKey: string;
};

const FILTER_KEY_MAP: FilterKeyEntry[] = [
  { filterKey: "alphabetic", categoryKey: "alphabetical" },
  { filterKey: "index", categoryKey: "exchange" },
  { filterKey: "sector", categoryKey: "sector" },
  { filterKey: "mktcap", categoryKey: "mktCapType" },
];

type ChipItem = {
  filterKey: keyof IFilters;
  value: string;
  label: string;
};

type StockActiveFilterChipsProps = {
  uiLabels?: StockListingUILabels;
  /**
   * Desktop default: unchanged original layout (hidden on small screens, visible md+).
   * "mobileHeader": single-line horizontal scroll for the mobile header bar only.
   */
  mode?: "desktop" | "mobileHeader";
};

const StockActiveFilterChips = memo(
  ({ uiLabels = {}, mode = "desktop" }: StockActiveFilterChipsProps) => {
    const { filterCategories, activeFilters, setActiveFilters, clearAll } =
      useStockListingFilters();

    const chips = useMemo<ChipItem[]>(() => {
      const result: ChipItem[] = [];
      for (const { filterKey, categoryKey } of FILTER_KEY_MAP) {
        const selectedValues = (activeFilters[filterKey] ?? []).map(String);
        if (selectedValues.length === 0) continue;

        const category = filterCategories.find((c) => c.key === categoryKey);
        for (const val of selectedValues) {
          const option = category?.options.find((o) => o.value === val);
          result.push({
            filterKey,
            value: val,
            label: option?.label ?? val,
          });
        }
      }
      return result;
    }, [activeFilters, filterCategories]);

    const removeChip = (filterKey: keyof IFilters, value: string) => {
      setActiveFilters((prev) => {
        const current = prev[filterKey] ?? [];
        const remaining = current.filter((v) => String(v) !== value);

        if (filterKey === "index") {
          const normalizedIndex = remaining
            .map((v) => (typeof v === "number" ? v : Number(v)))
            .filter((n) => !Number.isNaN(n));
          return { ...prev, [filterKey]: normalizedIndex };
        }

        return { ...prev, [filterKey]: remaining };
      });
    };

    if (chips.length === 0) return null;

    const chipSpanClassName =
      "text-[12px] text-[#1A1A1A] rounded-[4px] border border- [#203c57] bg-lgray-200 gap-[6px] px-[6px] py-[8px] h-[30px] flex items-center";

    if (mode === "mobileHeader") {
      return (
        <div className="flex w-full overflow-x-auto overflow-y-hidden pr-1">
          <div className="flex min-w-max flex-nowrap gap-2">
            {chips.map((chip) => (
              <span key={`${chip.filterKey}-${chip.value}`} className={chipSpanClassName}>
                {chip.label}
                <button
                  type="button"
                  onClick={() => removeChip(chip.filterKey, chip.value)}
                  className="ml-0.5 text-[11px] text-[#666] hover:text-[#1A1A1A]"
                  aria-label={`Remove ${chip.label} filter`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      );
    }

    // Desktop / default — matches pre–mobile-header implementation exactly
    return (
      <div className="hidden md:flex flex-wrap justify-between items-center gap-2 px-[10px] md:px-0 py-2 md:py-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-2 max-w-[88%]">
          {chips.map((chip) => (
            <span key={`${chip.filterKey}-${chip.value}`} className={chipSpanClassName}>
              {chip.label}
              <button
                type="button"
                onClick={() => removeChip(chip.filterKey, chip.value)}
                className="ml-0.5 text-[11px] text-[#666] hover:text-[#1A1A1A]"
                aria-label={`Remove ${chip.label} filter`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="text-link-primary text-sm bg-transparent border-l border-l-lgray-200 pt-[6px] pl-[6px] hover:opacity-80 transition-opacity"
        >
          {uiLabels.clearAllFiltersText ?? "Clear Filters"}
        </button>
      </div>
    );
  },
);

StockActiveFilterChips.displayName = "StockActiveFilterChips";

export default StockActiveFilterChips;
